// frontend/src/hooks/useClusterMutations.ts — the clusters write surface (create ·
// rename + reparent · disable · reactivate · set-default · reparent-preview · delete),
// mirroring useZoneMutations. Each mutating call invalidates the `['clusters',
// projectId]` prefix on success so list + detail refetch (FRONTEND-PATTERNS
// §Mutations + invalidation).
//
// Errors are NOT caught here — they bubble so the form shell / ConfirmDialog map
// the backend `code` (CLUSTER_NAME_EXISTS, CLUSTER_REPARENT_BLOCKED) through
// errors.json / the form's inline handling. 401 is the apiClient's job (ADR-003).
// No invalidate(['me']) — reference data doesn't affect the session.
//
// Divergence from zones (ADR-052): the PATCH carries `zone_id` too (rename +
// REPARENT in one form), and `useReparentClusterPreview` is the read-only
// proactive backing check the form fires when the operator changes the zone.

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type {
  CreateClusterRequest,
  ReparentClusterPreviewRequest,
  UpdateClusterRequest,
} from '@ems-app/schemas/clusters/requests';
import type { CreatedClusterResponse } from '@ems-app/schemas/clusters/responses';
import type { BackingImpact } from '@ems-app/schemas/accountability/responses';
import { apiClient } from '../lib/apiClient.js';
import { clustersKeys } from './useClusters.js';
import { refreshOnSettled } from '../lib/settled.js';

function useInvalidateClusters(projectId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: clustersKeys.all(projectId) });
}

/** POST …/clusters — create a cluster under a zone; returns its id. `is_default` clears the prior. */
export function useCreateCluster(
  projectId: string,
): UseMutationResult<CreatedClusterResponse, Error, CreateClusterRequest> {
  const invalidate = useInvalidateClusters(projectId);
  return useMutation({
    mutationFn: async (body) =>
      (await apiClient.post<CreatedClusterResponse>(`/projects/${projectId}/clusters`, body)).data,
    onSuccess: invalidate,
  });
}

/** PATCH …/clusters/:id — rename + reparent (name + zone_id). */
export function useUpdateCluster(
  projectId: string,
): UseMutationResult<void, Error, { clusterId: number; body: UpdateClusterRequest }> {
  const invalidate = useInvalidateClusters(projectId);
  return useMutation({
    mutationFn: async ({ clusterId, body }) => {
      await apiClient.patch(`/projects/${projectId}/clusters/${clusterId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** POST …/clusters/:id/{disable,reactivate} — lifecycle toggle (soft, unguarded). */
export function useSetClusterDisabled(
  projectId: string,
): UseMutationResult<void, Error, { clusterId: number; disabled: boolean }> {
  const invalidate = useInvalidateClusters(projectId);
  return useMutation({
    mutationFn: async ({ clusterId, disabled }) => {
      await apiClient.post(`/projects/${projectId}/clusters/${clusterId}/${disabled ? 'disable' : 'reactivate'}`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/clusters/:id/set-default — promote to the project default (clears the prior). */
export function useSetClusterDefault(projectId: string): UseMutationResult<void, Error, number> {
  const invalidate = useInvalidateClusters(projectId);
  return useMutation({
    mutationFn: async (clusterId) => {
      await apiClient.post(`/projects/${projectId}/clusters/${clusterId}/set-default`);
    },
    onSettled: refreshOnSettled(invalidate),
  });
}

/** POST …/clusters/:id/reparent/preview — read-only: would moving to `zone_id` orphan
 *  any cluster-level chain? (ADR-052 §5 proactive surfacing.) No invalidation — it
 *  mutates nothing; the form fires it when the zone field changes on an existing cluster. */
export function useReparentClusterPreview(
  projectId: string,
): UseMutationResult<BackingImpact, Error, { clusterId: number; zoneId: number }> {
  return useMutation({
    mutationFn: async ({ clusterId, zoneId }) => {
      const body: ReparentClusterPreviewRequest = { zone_id: zoneId };
      return (
        await apiClient.post<BackingImpact>(
          `/projects/${projectId}/clusters/${clusterId}/reparent/preview`,
          body,
        )
      ).data;
    },
  });
}

/** DELETE …/clusters/:id — only while nothing uses it; a refusal (409 REFERENCE_IN_USE) names the uses, read back by
 *  `ReferenceDeleteDialog`. */
export function useDeleteCluster(projectId: string): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`/projects/${projectId}/clusters/${id}`);
    },
    // The lists refetch — NOT the gone cluster's own read: refetched, it would 404 and the apiClient's not-found
    // redirect would beat the peek's close (the teams' delete precedent); a REFUSED delete leaves it, and it re-reads too.
    onSettled: refreshOnSettled((_d, id, err) => void qc.invalidateQueries({ queryKey: clustersKeys.all(projectId), predicate: err ? undefined : (q) => q.queryKey[2] !== id })),
  });
}
